import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './ScheduleManager.css';
import { useDataCache } from '../../../../../contexts/BreakdownDataCacheContext';
import { useFilmContext } from '../../../../../contexts/FilmContext';
import { SlotInfo, ScheduleInfo, CalDateInfo, SlotType, CopyScheduleResponse } from '../../../../../types/schedule.types';
import { useScheduleUpdates } from '../../../../../hooks/dataUpdates';
import DateSelect from '../../../../../components/DateSelect/DateSelect';
import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil, parseDate } from '../../../../../utils/dateUtils';
import { ReactComponent as BreakdownCopyIcon } from '../../../../../assets/svg/Breakdown/copy.svg';
import { ReactComponent as BreakdownDeleteIcon } from '../../../../../assets/svg/Breakdown/delete.svg';
import { ReactComponent as WaitIcon } from '../../../../../assets/svg/Breakdown/wait.svg';
import { copySchedule, getSchedulesByBreakdownId } from '../../../../../api/schedule';
import { getCurrentUserID, getCurrentUserOrgID } from '../../../../../utils/userUtils';
import ButtonDialog from '../../../../../components/ButtonDialog';

/** 右侧「计划管理」Tab：当前版本信息 + 同分场 schedule 列表 */
export interface ScheduleManagerProps {
  canEdit?: boolean;
  canCopy?: boolean;
  canManage?: boolean;
  schedule?: ScheduleInfo | null;
  slots?: SlotInfo[];
  calDates?: CalDateInfo[];
  scheduleID?: string;
  breakdownID?: string;
  currentDateInfo?: {
    shootingDayNumber: number | null;
    totalDays: number | null;
    shift: string | undefined;
    totalPages: number;
    featuredRolesQuantity: number;
    crowdRolesQuantity: number;
    cast: string;
  } | null;
  onScheduleRowsRefresh?: () => void;
  onDateShift?: (daysOffset: number) => void;
  onSelectSchedule?: (
    scheduleID: string,
    options?: { fromScheduleList?: boolean; openScenesListTab?: boolean; openDetailsTab?: boolean },
  ) => void;
  /** 确认删除指定拍摄计划（删除后由调用方处理刷新/导航） */
  onConfirmDeleteSchedule?: (scheduleID: string) => Promise<void>;
  /** 打开创建拍摄计划（未选中 schedule 时的「创建」按钮） */
  onOpenScheduleSearch?: () => void;
}

const ScheduleManager: React.FC<ScheduleManagerProps> = ({
  canEdit = false,
  canCopy = false,
  canManage = false,
  schedule,
  slots = [],
  calDates = [],
  scheduleID,
  breakdownID,
  currentDateInfo,
  onScheduleRowsRefresh,
  onDateShift,
  onSelectSchedule,
  onConfirmDeleteSchedule,
  onOpenScheduleSearch,
}) => {
  const { t } = useTranslation();
  const { patchSchedule } = useScheduleUpdates();
  const [scheduleIdPendingDelete, setScheduleIdPendingDelete] = useState<string | null>(null);
  const [deleteScheduleDialogVisible, setDeleteScheduleDialogVisible] = useState(false);
  const deleteAnchorRef = useRef<HTMLElement | null>(null);
  const deleteScheduleButtonRef = useRef<HTMLButtonElement>(null);

  const openDeleteDialog = (optionalScheduleId?: string, anchorEl?: HTMLElement | null) => {
    setScheduleIdPendingDelete(optionalScheduleId ?? null);
    deleteAnchorRef.current = anchorEl ?? null;
    setDeleteScheduleDialogVisible(true);
  };

  const handleDeleteScheduleDialogClose = () => {
    setDeleteScheduleDialogVisible(false);
    setScheduleIdPendingDelete(null);
    deleteAnchorRef.current = null;
  };

  const handleDeleteScheduleConfirm = async () => {
    const toDelete = scheduleIdPendingDelete ?? scheduleID;
    if (!toDelete || !onConfirmDeleteSchedule) return;
    try {
      await onConfirmDeleteSchedule(toDelete);
      setDeleteScheduleDialogVisible(false);
      setScheduleIdPendingDelete(null);
      deleteAnchorRef.current = null;
    } catch (error) {
      console.error('删除拍摄计划失败:', error);
      alert(t('schedule.deleteScheduleFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
    }
  };
  const [, setFilmName] = useState<string>('');
  const [scheduleName, setScheduleName] = useState<string>(() => {
    if (!scheduleID || !schedule) return '';
    return schedule.scheduleName || '';
  });
  // 不再使用 state，直接使用 schedule?.scheduleStartDate 和 schedule?.scheduleEndDate
  // const [scheduleStartDateLocal, setScheduleStartDateLocal] = useState<string | undefined>(schedule?.scheduleStartDate);
  // const [scheduleEndDateLocal, setScheduleEndDateLocal] = useState<string | undefined>(schedule?.scheduleEndDate);
  const [relatedSchedules, setRelatedSchedules] = useState<ScheduleInfo[]>([]);
  const [scheduleSoltsByScheduleId, setScheduleSoltsByScheduleId] = useState<Map<string, SlotInfo[]>>(new Map());
  const [editingScheduleNames, setEditingScheduleNames] = useState<Map<string, string>>(new Map());
  const [dateSelectVisible, setDateSelectVisible] = useState(false);
  const [dateSelectPosition, setDateSelectPosition] = useState({ top: 0, left: 0 });
  const [dateSelectType, setDateSelectType] = useState<'start' | 'end'>('start');
  const startDateButtonRef = useRef<HTMLDivElement>(null);
  const endDateButtonRef = useRef<HTMLDivElement>(null);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [copyingScheduleId, setCopyingScheduleId] = useState<string | null>(null);

  const { selectedFilmName, selectedFilmID } = useFilmContext();
  const { 
    getScheduleCache,
    subscribeScheduleCache,
    setScheduleCache,
    setSlotCache,
    setCalDateCache,
    getSlotCache,
    subscribeSlotCache
  } = useDataCache();

  // 从缓存获取影片名称（优先 FilmContext，且 scheduleFilmID 匹配时）
  useEffect(() => {
    if (schedule?.scheduleFilmID && schedule.scheduleFilmID === selectedFilmID && selectedFilmName) {
      setFilmName(selectedFilmName);
    } else {
      setFilmName('');
    }
  }, [schedule?.scheduleFilmID, selectedFilmID, selectedFilmName]);

  // 按拍摄计划名称升序排序：先拼音、后字母、再数字
  const getScheduleNameSortKey = useCallback((name: string) => {
    const n = (name || '').trim();
    if (!n) return '\uFFFF';
    const first = n[0];
    if (/[\u4e00-\u9fff]/.test(first)) return '0' + n;
    if (/[a-zA-Z]/.test(first)) return '1' + n;
    if (/[0-9]/.test(first)) return '2' + n;
    return '2' + n;
  }, []);

  // 刷新页面时缓存为空，需从 API 拉取该分场下的 schedule 列表并写入缓存（直链 /schedule/:id 时未必经过 BreakdownContainer）
  useEffect(() => {
    if (!breakdownID) return;
    const scheduleSlice = getScheduleCache(breakdownID);
    if (scheduleSlice?.schedules && scheduleSlice.schedules.length > 0) return;

    let cancelled = false;
    const fetchSchedules = async () => {
      try {
        const response = await getSchedulesByBreakdownId(breakdownID);
        const loadedSchedules = Array.isArray(response.data) ? response.data : [];
        if (!cancelled && loadedSchedules.length > 0) {
          setScheduleCache(breakdownID, { schedules: loadedSchedules });
        }
      } catch (error) {
        if (!cancelled) console.error('获取拍摄计划列表失败:', error);
      }
    };
    fetchSchedules();
    return () => { cancelled = true; };
  }, [breakdownID, getScheduleCache, setScheduleCache]);

  // 从缓存获取同分场下全部 schedule（含当前选中）
  useEffect(() => {
    if (breakdownID) {
      const scheduleSlice = getScheduleCache(breakdownID);
      if (scheduleSlice?.schedules && scheduleSlice.schedules.length > 0) {
        const sorted = [...scheduleSlice.schedules]
          .sort((a, b) => {
            const keyA = getScheduleNameSortKey(a.scheduleName || '');
            const keyB = getScheduleNameSortKey(b.scheduleName || '');
            return keyA.localeCompare(keyB, 'zh-CN', { numeric: true, sensitivity: 'base' });
          });
        setRelatedSchedules(sorted);
        
        const namesMap = new Map<string, string>();
        sorted.forEach((scheduleRow) => {
          namesMap.set(scheduleRow.scheduleID || '', scheduleRow.scheduleName || t('schedule.untitled'));
        });
        setEditingScheduleNames(namesMap);
        
        const slotsMap = new Map<string, SlotInfo[]>();
        sorted.forEach((scheduleRow) => {
          if (scheduleRow.scheduleID) {
            const slotCache = getSlotCache(scheduleRow.scheduleID);
            if (slotCache?.slots) {
              slotsMap.set(scheduleRow.scheduleID, slotCache.slots);
            } else {
              slotsMap.set(scheduleRow.scheduleID, []);
            }
          }
        });
        setScheduleSoltsByScheduleId(slotsMap);
      } else {
        setRelatedSchedules([]);
        setEditingScheduleNames(new Map());
        setScheduleSoltsByScheduleId(new Map());
      }
    }
  }, [breakdownID, scheduleID, getScheduleCache, getSlotCache, getScheduleNameSortKey, t]);

  // 订阅 schedule 列表缓存
  useEffect(() => {
    if (!breakdownID) return;
    
    const updateRelatedSchedules = () => {
      const scheduleSlice = getScheduleCache(breakdownID);
      if (scheduleSlice?.schedules && scheduleSlice.schedules.length > 0) {
        const sorted = [...scheduleSlice.schedules]
          .sort((a, b) => {
            const keyA = getScheduleNameSortKey(a.scheduleName || '');
            const keyB = getScheduleNameSortKey(b.scheduleName || '');
            return keyA.localeCompare(keyB, 'zh-CN', { numeric: true, sensitivity: 'base' });
          });
        setRelatedSchedules(sorted);
        
        const namesMap = new Map<string, string>();
        sorted.forEach((scheduleRow) => {
          namesMap.set(scheduleRow.scheduleID || '', scheduleRow.scheduleName || t('schedule.untitled'));
        });
        setEditingScheduleNames(namesMap);
        
        const slotsMap = new Map<string, SlotInfo[]>();
        sorted.forEach((scheduleRow) => {
          if (scheduleRow.scheduleID) {
            const slotCache = getSlotCache(scheduleRow.scheduleID);
            if (slotCache?.slots) {
              slotsMap.set(scheduleRow.scheduleID, slotCache.slots);
            } else {
              slotsMap.set(scheduleRow.scheduleID, []);
            }
          }
        });
        setScheduleSoltsByScheduleId(slotsMap);
      } else {
        setRelatedSchedules([]);
        setEditingScheduleNames(new Map());
        setScheduleSoltsByScheduleId(new Map());
      }
    };

    updateRelatedSchedules();
    const unsubscribe = subscribeScheduleCache(breakdownID, updateRelatedSchedules);
    return unsubscribe;
  }, [breakdownID, getScheduleCache, getSlotCache, getScheduleNameSortKey, subscribeScheduleCache, t]);

  // 订阅各 schedule 的 slot 缓存（列表卡片杀青日等依赖任务）
  useEffect(() => {
    if (!breakdownID) return;
    
    const scheduleSlice = getScheduleCache(breakdownID);
    if (!scheduleSlice?.schedules) return;
    
    const unsubscribes: (() => void)[] = [];
    scheduleSlice.schedules.forEach((scheduleRow) => {
      if (scheduleRow.scheduleID) {
        const unsubscribe = subscribeSlotCache(scheduleRow.scheduleID, () => {
          const updatedSoltsMap = new Map(scheduleSoltsByScheduleId);
          const slotCache = getSlotCache(scheduleRow.scheduleID!);
          if (slotCache?.slots) {
            updatedSoltsMap.set(scheduleRow.scheduleID!, slotCache.slots);
            setScheduleSoltsByScheduleId(updatedSoltsMap);
          }
        });
        unsubscribes.push(unsubscribe);
      }
    });
    
    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [breakdownID, getScheduleCache, getSlotCache, subscribeSlotCache, scheduleSoltsByScheduleId]);

  // 与父组件 / 缓存中的拍摄计划名称保持同步；无选中拍摄计划时清空，避免删除后仍显示旧名称
  useEffect(() => {
    if (!scheduleID || !schedule) {
      setScheduleName('');
      return;
    }
    setScheduleName(schedule.scheduleName || '');
  }, [scheduleID, schedule, schedule?.scheduleName]);

  // 处理拍摄计划名称修改
  const handleScheduleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScheduleName(e.target.value);
  };

  const handleScheduleNameBlur = async () => {
    if (!scheduleID) return;
    if (scheduleName !== schedule?.scheduleName) {
      try {
        // 使用统一的更新 hook，自动更新缓存和通知订阅者
        await patchSchedule(scheduleID, { scheduleName: scheduleName });
        // 缓存已自动更新，通过 onScheduleRowsRefresh 通知父组件刷新
        onScheduleRowsRefresh?.();
      } catch (error) {
        console.error('更新拍摄计划名称失败:', error);
        setScheduleName(schedule?.scheduleName || '');
      }
    }
  };

  const handleScheduleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  // 处理日期选择
  const handleDateSelectClick = (type: 'start' | 'end') => {
    setDateSelectType(type);
    
    // 根据类型获取对应的日期和按钮 ref
    const dateStr =
      type === 'start'
        ? (computedScheduleStartDate ?? schedule?.scheduleStartDate)
        : (computedScheduleEndDate ?? schedule?.scheduleEndDate);
    const buttonRef = type === 'start' ? startDateButtonRef : endDateButtonRef;
    
    // 如果有日期值，设置为该日期所在的年月；否则使用当前年月
    if (dateStr) {
      const date = parseDate(dateStr);
      setCurrentYear(date.getFullYear());
      setCurrentMonth(date.getMonth() + 1);
    } else {
      setCurrentYear(new Date().getFullYear());
      setCurrentMonth(new Date().getMonth() + 1);
    }
    
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDateSelectPosition({
        top: rect.bottom + 6,
        left: rect.left
      });
      setDateSelectVisible(true);
    }
  };

  const handleDateSelect = async (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (scheduleID) {
      try {
        // 使用统一的更新 hook，自动更新缓存和通知订阅者
        if (dateSelectType === 'start') {
          await patchSchedule(scheduleID, { scheduleStartDate: dateStr });
          // 缓存更新会驱动 schedule 对象刷新，无需本地冗余 state
        } else {
          await patchSchedule(scheduleID, { scheduleEndDate: dateStr });
          // 缓存更新会驱动 schedule 对象刷新，无需本地冗余 state
        }
        // 注意：onScheduleRowsRefresh 会在批量更新完成后由 onBatchUpdateComplete 调用
        // 这里不调用，避免重复刷新
      } catch (error) {
        console.error('更新拍摄计划日期失败:', error);
        throw error; // 抛出错误，让 DateSelect 组件处理
      }
    }
    // 注意：不在这里关闭弹窗，由 DateSelect 组件控制
  };

  // 处理选择月份（当启用日期选择时，选择月份后显示日期列表）
  const handleSelectMonth = (year: number, month: number) => {
    setCurrentYear(year);
    setCurrentMonth(month);
    // 不关闭弹窗，继续显示日期选择
  };

  // 格式化日期显示
  // 使用工具函数格式化日期，避免时区问题
  const formatDate = (dateStr?: string) => {
    return formatDateUtil(dateStr || '');
  };

  // 格式化日期时间显示
  const formatDateTime = (dateTimeStr?: string) => {
    return formatDateTimeUtil(dateTimeStr || '');
  };

  // 根据 slot 推算某日杀青日期（仅 SHOOT；NOTE 不参与）
  const getScheduleWrapDateFromSolts = (scheduleRowId: string, scheduleRow: ScheduleInfo) => {
    if (scheduleRowId === scheduleID) {
      return schedule?.scheduleEndDate || scheduleRow.scheduleEndDate;
    }
    const slots = scheduleSoltsByScheduleId.get(scheduleRowId) || [];
    const slotTypeSolts = slots.filter(
      pt => pt.slotType === SlotType.SHOOT || !pt.slotType
    );
    if (slotTypeSolts.length === 0) return scheduleRow.scheduleEndDate;
    const dates = slotTypeSolts
      .map(pt => pt.slotEndDay)
      .filter(Boolean)
      .map(date => date?.split('T')[0])
      .filter(Boolean)
      .sort();
    return dates.length > 0 ? dates[dates.length - 1] : scheduleRow.scheduleEndDate;
  };

  // 右侧信息区：开机/杀青日期应随 slots 实时变化（仅以类型为 SHOOT 的 slot 为准）
  const computedScheduleStartDate = useMemo(() => {
    const slotTypeSolts = slots.filter(pt => pt.slotType === SlotType.SHOOT || !pt.slotType);
    const dates = slotTypeSolts
      .map(pt => pt.slotDate)
      .filter(Boolean)
      .map(d => d!.split('T')[0])
      .filter(Boolean)
      .sort();
    return dates.length > 0 ? dates[0] : undefined;
  }, [slots]);

  const computedScheduleEndDate = useMemo(() => {
    const slotTypeSolts = slots.filter(pt => pt.slotType === SlotType.SHOOT || !pt.slotType);
    const dates = slotTypeSolts
      .map(pt => pt.slotEndDay)
      .filter(Boolean)
      .map(d => d!.split('T')[0])
      .filter(Boolean)
      .sort();
    return dates.length > 0 ? dates[dates.length - 1] : undefined;
  }, [slots]);

  // 当用户在日历中调整 slots 导致最早/最晚日期变化时，自动同步 schedule.scheduleStartDate / scheduleEndDate（缓存 + 服务器）
  const lastAutoSyncedRangeRef = useRef<{ start?: string; end?: string }>({});
  const autoSyncTimerRef = useRef<number | null>(null);
  const isAutoSyncingRef = useRef(false);
  useEffect(() => {
    if (!scheduleID) return;
    if (!computedScheduleStartDate || !computedScheduleEndDate) return;

    const curStart = (schedule?.scheduleStartDate || '').split('T')[0];
    const curEnd = (schedule?.scheduleEndDate || '').split('T')[0];
    const nextStart = computedScheduleStartDate;
    const nextEnd = computedScheduleEndDate;

    // 避免无效范围与循环更新
    if (nextStart > nextEnd) return;
    if (curStart === nextStart && curEnd === nextEnd) return;
    if (
      lastAutoSyncedRangeRef.current.start === nextStart &&
      lastAutoSyncedRangeRef.current.end === nextEnd
    ) {
      return;
    }
    if (isAutoSyncingRef.current) return;

    if (autoSyncTimerRef.current != null) {
      window.clearTimeout(autoSyncTimerRef.current);
      autoSyncTimerRef.current = null;
    }
    autoSyncTimerRef.current = window.setTimeout(() => {
      autoSyncTimerRef.current = null;
      isAutoSyncingRef.current = true;
      lastAutoSyncedRangeRef.current = { start: nextStart, end: nextEnd };

      // 单次请求同时更新 start/end，避免两次 API 与中间态
      patchSchedule(scheduleID, { scheduleStartDate: nextStart, scheduleEndDate: nextEnd })
        .catch((error) => {
          console.error('自动同步拍摄计划开机/杀青日期失败:', error);
          // 失败允许后续再次尝试
          lastAutoSyncedRangeRef.current = {};
        })
        .finally(() => {
          isAutoSyncingRef.current = false;
        });
    }, 400);
  }, [scheduleID, schedule?.scheduleStartDate, schedule?.scheduleEndDate, computedScheduleStartDate, computedScheduleEndDate, patchSchedule]);

  const handleScheduleListNameChange = (scheduleRowId: string, value: string) => {
    const newNames = new Map(editingScheduleNames);
    newNames.set(scheduleRowId, value);
    setEditingScheduleNames(newNames);
  };


  const handleScheduleListNameBlur = async (scheduleRowId: string) => {
    const newName = editingScheduleNames.get(scheduleRowId);
    if (!newName || newName === relatedSchedules.find((s) => s.scheduleID === scheduleRowId)?.scheduleName) {
      return;
    }
    
    try {
      await patchSchedule(scheduleRowId, { scheduleName: newName });
      const row = relatedSchedules.find((s) => s.scheduleID === scheduleRowId);
      if (row) {
        const updatedSchedules = relatedSchedules.map((s) => 
          s.scheduleID === scheduleRowId ? { ...s, scheduleName: newName } : s
        );
        setRelatedSchedules(updatedSchedules);
      }
      if (onScheduleRowsRefresh) {
        onScheduleRowsRefresh();
      }
    } catch (error) {
      console.error('更新拍摄计划名称失败:', error);
      const row = relatedSchedules.find((s) => s.scheduleID === scheduleRowId);
      if (row) {
        const newNames = new Map(editingScheduleNames);
        newNames.set(scheduleRowId, row.scheduleName || t('schedule.untitled'));
        setEditingScheduleNames(newNames);
      }
    }
  };

  const handleScheduleListNameKeyDown = (scheduleRowId: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      const row = relatedSchedules.find((s) => s.scheduleID === scheduleRowId);
      if (row) {
        const newNames = new Map(editingScheduleNames);
        newNames.set(scheduleRowId, row.scheduleName || t('schedule.untitled'));
        setEditingScheduleNames(newNames);
      }
      e.currentTarget.blur();
    }
  };

  const handleCopyCurrentSchedule = async () => {
    if (scheduleID) await handleCopyScheduleFrom(scheduleID);
  };

  const handleCopyScheduleFrom = async (sourceScheduleID: string, switchToNew: boolean = true) => {
    if (!sourceScheduleID || !breakdownID) return;

    try {
      setCopyingScheduleId(sourceScheduleID);

      const currentUserID = getCurrentUserID();
      const currentUserOrgID = getCurrentUserOrgID();
      if (!currentUserID) {
        throw new Error('无法获取当前用户信息');
      }
      if (!currentUserOrgID) {
        throw new Error('无法获取用户组织信息');
      }

      const sourceSchedule = relatedSchedules.find((s) => s.scheduleID === sourceScheduleID);
      const originalName = sourceSchedule?.scheduleName || t('schedule.untitled');
      let suffixNumber = 1;
      let newScheduleName = `${originalName}${t('common.copyNameSuffix')}`;

      const existingScheduleNames = relatedSchedules.map((s) => s.scheduleName);
      while (existingScheduleNames.includes(newScheduleName)) {
        suffixNumber++;
        newScheduleName = `${originalName}${t('common.copyNameSuffixNumbered', { n: suffixNumber })}`;
      }

      const response = await copySchedule({
        sourceScheduleID,
        targetScheduleName: newScheduleName,
      });

      const data = response.data as CopyScheduleResponse;
      const newScheduleId = data.scheduleID;

      if (!newScheduleId) {
        throw new Error('复制拍摄计划失败：未返回 scheduleID');
      }

      const { slots: newSlots, calDates: newCalDates, ...copiedScheduleFields } = data;
      const newScheduleInfo = copiedScheduleFields as ScheduleInfo;

      if (breakdownID) {
        const cache = getScheduleCache(breakdownID);
        const existing = cache?.schedules ?? [];
        if (!existing.some(p => p.scheduleID === newScheduleId)) {
          setScheduleCache(breakdownID, { schedules: [...existing, newScheduleInfo] });
        }
        if (newSlots !== undefined && Array.isArray(newSlots)) {
          setSlotCache(newScheduleId, { scheduleInfo: newScheduleInfo, slots: newSlots });
        }
        if (newCalDates !== undefined && Array.isArray(newCalDates)) {
          setCalDateCache(newScheduleId, { calDates: newCalDates });
        }
      }

      setCopyingScheduleId(null);

      if (switchToNew && onSelectSchedule) {
        onSelectSchedule(newScheduleId, { openDetailsTab: true });
      }

      if (onScheduleRowsRefresh) {
        await onScheduleRowsRefresh();
      }
    } catch (error) {
      console.error('复制拍摄计划失败:', error);
      alert(t('schedule.duplicateScheduleFailed') + (error instanceof Error ? error.message : t('common.unknownError')));
      setCopyingScheduleId(null);
    }
  };

  // 拍摄天数：与日历「当前/总日」分母同源（Calendar → DayInfo → currentDateInfo），信息区与列表卡片共用
  const shootingDaysDisplay =
    currentDateInfo?.totalDays != null ? String(currentDateInfo.totalDays) : '';

  return (
    <div className="scheduleDetails-details-container">
      {/* 拍摄计划信息区 / 无选中 schedule 时的提示 */}
      <div className="scheduleDetails-scheduleInfo">
        {scheduleID ? (
          <>
            <div className="scheduleDetails-scheduleInfo-field">
              <div className="scheduleDetails-scheduleInfo-field-label">{t('schedule.currentVersion')}</div>
              <input
                type="text"
                className="scheduleDetails-scheduleInfo-field-input scheduleDetails-scheduleInfo-field-input-editable"
                value={scheduleName}
                onChange={handleScheduleNameChange}
                onBlur={handleScheduleNameBlur}
                onKeyDown={handleScheduleNameKeyDown}
                placeholder={t('schedule.untitled')}
                readOnly={!canEdit}
              />
              {canCopy && (copyingScheduleId === scheduleID ? (
                <button
                  className="scheduleDetails-scheduleInfo-action-btn"
                  disabled
                  title={t('schedule.duplicating')}
                >
                  <WaitIcon className="scheduleDetails-scheduleInfo-action-icon copying" />
                </button>
              ) : (
                <button
                  className="scheduleDetails-scheduleInfo-action-btn"
                  onClick={handleCopyCurrentSchedule}
                  title={t('schedule.duplicateSchedule')}
                >
                  <BreakdownCopyIcon className="scheduleDetails-scheduleInfo-action-icon" />
                </button>
              ))}
              {canManage && <button
                ref={deleteScheduleButtonRef}
                className="scheduleDetails-scheduleInfo-action-btn"
                title={t('schedule.deleteCurrentVersion')}
                onClick={() => openDeleteDialog()}
              >
                <BreakdownDeleteIcon className="scheduleDetails-scheduleInfo-action-icon" />
              </button>}
            </div>
            <div className="scheduleDetails-scheduleInfo-field-row">
              <div className="scheduleDetails-scheduleInfo-field">
                <div className="scheduleDetails-scheduleInfo-field-label">{t('schedule.startDate')}</div>
                <div
                  ref={startDateButtonRef}
                  className="scheduleDetails-scheduleInfo-field-date"
                  onClick={() => {
                    if (canEdit) handleDateSelectClick('start');
                  }}
                >
                  {formatDate(computedScheduleStartDate ?? schedule?.scheduleStartDate) || ''}
                </div>
              </div>
              <div className="scheduleDetails-scheduleInfo-field">
                <div className="scheduleDetails-scheduleInfo-field-label">{t('schedule.wrapDate')}</div>
                <div
                  ref={endDateButtonRef}
                  className="scheduleDetails-scheduleInfo-field-date readonly"
                  tabIndex={-1}
                >
                  {formatDate(computedScheduleEndDate ?? schedule?.scheduleEndDate) || ''}
                </div>
              </div>
              <div className="scheduleDetails-scheduleInfo-field">
                <div className="scheduleDetails-scheduleInfo-field-label">{t('schedule.shootingDays')}</div>
                <input
                  type="text"
                  className="scheduleDetails-scheduleInfo-field-input"
                  value={shootingDaysDisplay}
                  readOnly
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="scheduleDetails-scheduleInfo-field">
              <div className="scheduleDetails-scheduleInfo-field-label">{t('schedule.currentVersion')}</div>
              <input
                type="text"
                className="scheduleDetails-scheduleInfo-field-input"
                readOnly
                value=""
                placeholder={t('schedule.selectOrCreateScheduleHint')}
              />
            </div>
            <div className="scheduleDetails-scheduleInfo-empty">
              <span className="scheduleDetails-scheduleInfo-empty-text">请在下方列表选择切换到其他拍摄计划，或 </span>
              {canEdit && <button
                type="button"
                className="scheduleDetails-scheduleInfo-empty-create-btn"
                onClick={onOpenScheduleSearch}
              >
                创建
              </button>}
              <span className="scheduleDetails-scheduleInfo-empty-text"> 新的拍摄计划</span>
            </div>
          </>
        )}
      </div>

      {/* 拍摄计划列表区 - 两列卡片 */}
      <div className="scheduleDetails-scheduleList">
        <div className="scheduleDetails-scheduleList-Card-header">{t('schedule.allSchedule')}</div>
        <div className="scheduleDetails-scheduleList-grid">
          {relatedSchedules.map((scheduleRow) => {
            const scheduleRowId = scheduleRow.scheduleID || '';
            const scheduleRowName =
              editingScheduleNames.get(scheduleRowId) || scheduleRow.scheduleName || t('schedule.untitled');
            const wrapDateFromSolts = getScheduleWrapDateFromSolts(scheduleRowId, scheduleRow);
            // 当前 schedule：与信息区同源（currentDateInfo）；其它：列表中的 scheduleTotalDays
            const scheduleRowTotalDays =
              scheduleRowId === scheduleID
                ? shootingDaysDisplay || '-'
                : scheduleRow.scheduleTotalDays != null
                  ? String(scheduleRow.scheduleTotalDays)
                  : '-';

            return (
              <div 
                key={scheduleRowId} 
                className={`scheduleDetails-scheduleList-Card${scheduleRowId === scheduleID ? ' scheduleDetails-scheduleList-Card--selected' : ''}`}
                onClick={(e) => {
                  if ((e.target as HTMLElement).tagName === 'INPUT') return;
                  if (onSelectSchedule && scheduleRowId) onSelectSchedule(scheduleRowId, { fromScheduleList: true });
                }}
              >
                {/* 第一行：拍摄计划名称 + 最后更新；第二行：开机日期、杀青日期、拍摄天数（与最后更新列对齐） */}
                <div className="scheduleDetails-scheduleList-Card-fields">
                  <input
                    type="text"
                    className="scheduleDetails-scheduleList-Card-name-input"
                    value={scheduleRowName}
                    onChange={(e) => handleScheduleListNameChange(scheduleRowId, e.target.value)}
                    onBlur={() => handleScheduleListNameBlur(scheduleRowId)}
                    onKeyDown={(e) => handleScheduleListNameKeyDown(scheduleRowId, e)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={t('schedule.untitled')}
                    readOnly={!canEdit}
                  />
                  <div className="scheduleDetails-scheduleList-Card-field scheduleDetails-scheduleList-Card-lastUpdate">
                    <span className="scheduleDetails-scheduleList-Card-field-label">{t('schedule.lastUpdate')}</span>
                    <span className="scheduleDetails-scheduleList-Card-field-value">{formatDateTime(scheduleRow.scheduleUpdateTime)}</span>
                  </div>
                  <div className="scheduleDetails-scheduleList-Card-field scheduleDetails-scheduleList-Card-startDate">
                    <span className="scheduleDetails-scheduleList-Card-field-label">{t('schedule.startDate')}</span>
                    <span className="scheduleDetails-scheduleList-Card-field-value">{formatDate(scheduleRow.scheduleStartDate) || '—'}</span>
                  </div>
                  <div className="scheduleDetails-scheduleList-Card-field scheduleDetails-scheduleList-Card-wrapDate">
                    <span className="scheduleDetails-scheduleList-Card-field-label">{t('schedule.wrapDate')}</span>
                    <span className="scheduleDetails-scheduleList-Card-field-value">{formatDate(wrapDateFromSolts) || '—'}</span>
                  </div>
                  <div className="scheduleDetails-scheduleList-Card-field scheduleDetails-scheduleList-Card-shootingDays">
                    <span className="scheduleDetails-scheduleList-Card-field-label">{t('schedule.shootingDays')}</span>
                    <span className="scheduleDetails-scheduleList-Card-field-value">{scheduleRowTotalDays}</span>
                  </div>
                  <div className="scheduleDetails-scheduleList-Card-actions">
                    {/* 暂时隐藏：复制、删除按钮，功能和逻辑保留 */}
                    {/* {copyingScheduleId === scheduleRowId ? (
                      <button
                        type="button"
                        className="scheduleDetails-scheduleInfo-action-btn"
                        disabled
                        title={t('schedule.duplicating')}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <WaitIcon className="scheduleDetails-scheduleInfo-action-icon copying" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="scheduleDetails-scheduleInfo-action-btn"
                        title={t('schedule.duplicateSchedule')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyScheduleFrom(scheduleRowId, false);
                        }}
                      >
                        <BreakdownCopyIcon className="scheduleDetails-scheduleInfo-action-icon" />
                      </button>
                    )}
                    <button
                      type="button"
                      className="scheduleDetails-scheduleInfo-action-btn"
                      title={t('schedule.deleteCurrentVersion')}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (scheduleRowId) openDeleteDialog(scheduleRowId, e.currentTarget as HTMLElement);
                      }}
                    >
                      <BreakdownDeleteIcon className="scheduleDetails-scheduleInfo-action-icon" />
                    </button> */}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {relatedSchedules.length === 0 && (
          <div className="scheduleDetails-scheduleList-empty">暂无其他拍摄计划</div>
        )}
      </div>

      {/* DateSelect 弹窗 */}
      <DateSelect
        visible={dateSelectVisible}
        onClose={() => setDateSelectVisible(false)}
        position={dateSelectPosition}
        selectedYear={currentYear}
        selectedMonth={currentMonth}
        onSelectMonth={handleSelectMonth}
        enableDateSelection={true}
        selectedDate={
          dateSelectType === 'start'
            ? ((computedScheduleStartDate ?? schedule?.scheduleStartDate) ? parseDate((computedScheduleStartDate ?? schedule?.scheduleStartDate)!) : null)
            : ((computedScheduleEndDate ?? schedule?.scheduleEndDate) ? parseDate((computedScheduleEndDate ?? schedule?.scheduleEndDate)!) : null)
        }
        onSelectDate={handleDateSelect}
        enableDateShiftConfirm={true}
        originalDate={
          dateSelectType === 'start'
            ? ((computedScheduleStartDate ?? schedule?.scheduleStartDate) ? parseDate((computedScheduleStartDate ?? schedule?.scheduleStartDate)!) : null)
            : ((computedScheduleEndDate ?? schedule?.scheduleEndDate) ? parseDate((computedScheduleEndDate ?? schedule?.scheduleEndDate)!) : null)
        }
        dateFieldType={dateSelectType}
        scheduleID={scheduleID}
        slots={slots}
        calDates={calDates}
        onDateShift={onDateShift}
        onBatchUpdateComplete={async () => {
          if (onScheduleRowsRefresh) {
            await onScheduleRowsRefresh();
          }
        }}
      />

      <ButtonDialog
        visible={deleteScheduleDialogVisible}
        onClose={handleDeleteScheduleDialogClose}
        onConfirm={handleDeleteScheduleConfirm}
        message={t('schedule.confirmDeleteSchedule')}
        confirmText={t('common.confirm')}
        cancelText={t('common.cancel')}
        buttonRef={deleteScheduleButtonRef}
        anchorRef={deleteAnchorRef}
        align="right"
      />
    </div>
  );
};
export default ScheduleManager;
